import React from 'react';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { CheckSquare } from 'lucide-react';
import { AdminTenantFormData, SPECIALISATION_OPTIONS } from './types';

interface Props {
  data: AdminTenantFormData;
  updateData: (updates: Partial<AdminTenantFormData>) => void;
}

const StepBotConfig: React.FC<Props> = ({ data, updateData }) => {
  const toggleSpecialisation = (value: string) => {
    const current = data.specialisations;
    if (current.includes(value)) {
      updateData({ specialisations: current.filter((s) => s !== value) });
    } else {
      updateData({ specialisations: [...current, value] });
    }
  };

  return (
    <div className="space-y-6">
      {/* Specialisations */}
      <div className="space-y-3">
        <Label>Specialisations</Label>
        <p className="text-sm text-muted-foreground">
          Select the services this business specializes in
        </p>
        <div className="grid gap-2 sm:grid-cols-2">
          {SPECIALISATION_OPTIONS.map((option) => (
            <label
              key={option.value}
              className="flex items-center gap-3 p-3 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
            >
              <Checkbox
                checked={data.specialisations.includes(option.value)}
                onCheckedChange={() => toggleSpecialisation(option.value)}
              />
              <span className="text-sm">{option.label}</span>
            </label>
          ))}
        </div>

        {data.specialisations.length > 0 && (
          <div className="flex items-center gap-2 mt-2">
            <CheckSquare className="h-4 w-4 text-primary" />
            <span className="text-sm text-muted-foreground">
              {data.specialisations.length} selected
            </span>
          </div>
        )}
      </div>

      {/* Other Specialisations */}
      <div className="space-y-2">
        <Label htmlFor="specialisationsOther">Other Specialisations</Label>
        <Textarea
          id="specialisationsOther"
          value={data.specialisationsOther}
          onChange={(e) => updateData({ specialisationsOther: e.target.value })}
          placeholder="Any other services not listed above..."
          rows={2}
        />
      </div>

      {/* Topics to Avoid */}
      <div className="space-y-2">
        <Label htmlFor="doNotMention">Topics to Avoid</Label>
        <p className="text-sm text-muted-foreground">
          Topics or competitors the assistant should not mention
        </p>
        <Textarea
          id="doNotMention"
          value={data.doNotMention}
          onChange={(e) => updateData({ doNotMention: e.target.value })}
          placeholder="e.g., Competitor names, sensitive topics, outdated services..."
          rows={3}
        />
      </div>

      {/* Internal Notes */}
      <div className="space-y-2">
        <Label htmlFor="internalNotes">
          Internal Notes
          <span className="ml-2 text-xs text-muted-foreground font-normal">
            (not visible to client)
          </span>
        </Label>
        <Textarea
          id="internalNotes"
          value={data.internalNotes}
          onChange={(e) => updateData({ internalNotes: e.target.value })}
          placeholder="Notes for the team building this assistant..."
          rows={3}
        />
      </div>
    </div>
  );
};

export default StepBotConfig;
